import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injector, inject, Service } from '@angular/core';
import { HypecastAuthenticationService } from '@app/shared/authentication/services/authentication.service';
import { HypecastOAuthService } from '@app/shared/authentication/services/hypecast-oauth.service';
import { __ } from '@app/shared/functions/object.functions';
import { ForbiddenService } from '@app/shared/services/forbidden.service';
import { BehaviorSubject, Observable, of, Subscriber, throwError } from 'rxjs';
import { catchError, filter, finalize, mergeMap, switchMap, take, timeout } from 'rxjs/operators';

import { SKIP_TOKEN_REFRESH } from './http-context';

/**
 * Sends the user back through the login flow without awaiting the result.
 *
 * `login()` rejects with the bare value `false` when the refresh needs user
 * interaction (see HypecastOAuthService). Both call sites below are deliberately
 * fire-and-forget — the HTTP error is already on its way to the caller — but an
 * un-awaited rejection surfaces in RUM as `Non-Error thrown false` with no stack,
 * which is noise that looks like a crash. Swallow it here; the flow itself has
 * already done everything it can.
 */
function startLoginWithoutAwaiting(oauthService: HypecastOAuthService): void {
  void Promise.resolve(oauthService.login()).catch(() => undefined);
}

@Service({ autoProvided: false })
export class AuthorizationInterceptor implements HttpInterceptor {
  private injector = inject(Injector);
  private forbiddenService = inject(ForbiddenService);

  private isRefreshingToken = false;
  private tokenRefreshedSubject = new BehaviorSubject<boolean | null>(null);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const authenticationService = this.injector.get(HypecastAuthenticationService);
    const oauthService = this.injector.get(HypecastOAuthService);

    // Proactively check if token is expired or will expire soon (within 5 minutes)
    const tokenExpiresAt = oauthService.getAccessTokenExpiration();
    const now = Date.now();
    const fiveMinutesFromNow = now + 5 * 60 * 1000;

    // If token is expired or will expire soon, refresh it before making the request
    if (
      tokenExpiresAt &&
      tokenExpiresAt < fiveMinutesFromNow &&
      !__.IsNullOrUndefinedOrEmpty(oauthService.getRefreshToken())
    ) {
      // Token needs refresh - do it proactively
      if (this.isRefreshingToken) {
        // Token refresh is already in progress, queue this request
        return this.tokenRefreshedSubject.pipe(
          filter((value): value is boolean => value !== null),
          take(1),
          timeout(10_000),
          catchError(() => of(false)),
          switchMap((success) => {
            if (success) {
              // Retry the request with new token
              return next.handle(
                request.clone({
                  setHeaders: { Authorization: `Bearer ${authenticationService.getAccessToken()}` },
                }),
              );
            } else {
              return throwError(() => new Error('Token refresh failed'));
            }
          }),
        );
      }

      // Start proactive token refresh
      this.isRefreshingToken = true;
      this.tokenRefreshedSubject.next(null);

      return new Observable<boolean>((subscriber: Subscriber<boolean>) => {
        oauthService
          .refreshTokenOnly()
          .then((success: boolean) => {
            if (success) {
              subscriber.next(true);
              subscriber.complete();
            } else {
              subscriber.error(new Error('Proactive token refresh failed'));
            }
          })
          .catch((error: unknown) => {
            subscriber.error(error);
          });
      }).pipe(
        mergeMap(() => {
          // Token refresh successful
          this.tokenRefreshedSubject.next(true);

          // Make the original request with new token
          return next.handle(
            request.clone({
              setHeaders: { Authorization: `Bearer ${authenticationService.getAccessToken()}` },
            }),
          );
        }),
        catchError((_error: unknown) => {
          // Proactive refresh failed, let the request go through and handle 401 if it occurs
          this.tokenRefreshedSubject.next(false);

          // Continue with original request - if it fails with 401, it will be caught below
          return next.handle(request.clone()).pipe(
            catchError((response: HttpErrorResponse) => {
              if (response.status === 401) {
                return this.handle401Error(request, next, authenticationService, oauthService, response);
              }
              throw response;
            }),
          );
        }),
        finalize(() => {
          this.isRefreshingToken = false;
        }),
      );
    }

    return next.handle(request.clone()).pipe(
      catchError((response: HttpErrorResponse) => {
        if (
          response.status === 401 &&
          request.method.toLowerCase() !== 'options' &&
          !request.context.get(SKIP_TOKEN_REFRESH)
        ) {
          return this.handle401Error(request, next, authenticationService, oauthService, response);
        }

        if (response.status === 403) {
          this.forbiddenService.forbid();
        }

        throw response;
      }),
    );
  }

  private handle401Error(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    authenticationService: HypecastAuthenticationService,
    oauthService: HypecastOAuthService,
    response: HttpErrorResponse,
  ): Observable<HttpEvent<unknown>> {
    // Queue multiple requests and send out only one token request
    if (this.isRefreshingToken) {
      // Token refresh is already in progress, queue this request
      return this.tokenRefreshedSubject.pipe(
        filter((value): value is boolean => value !== null),
        take(1),
        timeout(10_000),
        catchError(() => of(false)),
        switchMap((success) => {
          if (success) {
            // Retry the request with new token
            return next.handle(
              request.clone({
                setHeaders: { Authorization: `Bearer ${authenticationService.getAccessToken()}` },
              }),
            );
          } else {
            return throwError(() => new Error('Token refresh failed'));
          }
        }),
      );
    }

    // Start token refresh
    this.isRefreshingToken = true;
    this.tokenRefreshedSubject.next(null);

    return new Observable<boolean>((subscriber: Subscriber<boolean>) => {
      // Try to refresh token instead of full login
      oauthService
        .refreshTokenOnly()
        .then((success: boolean) => {
          if (success) {
            subscriber.next(true);
            subscriber.complete();
          } else {
            subscriber.error(new Error('Token refresh failed'));
          }
        })
        .catch((error: unknown) => {
          subscriber.error(error);
        });
    }).pipe(
      mergeMap(() => {
        // Token refresh successful
        this.tokenRefreshedSubject.next(true);

        // Retry the original request with new token
        return next.handle(
          request.clone({
            setHeaders: { Authorization: `Bearer ${authenticationService.getAccessToken()}` },
          }),
        );
      }),
      catchError((_error: unknown) => {
        // Token refresh failed
        this.tokenRefreshedSubject.next(false);

        // Check if we have a refresh token
        if (__.IsNullOrUndefinedOrEmpty(oauthService.getRefreshToken())) {
          // No refresh token, silently re-trigger SSO login flow to get new tokens
          authenticationService.logout();
          startLoginWithoutAwaiting(oauthService);
          return throwError(response);
        }

        // Refresh token exists but refresh failed, try full login silently
        return new Observable<unknown>((loginSubscriber: Subscriber<unknown>) => {
          oauthService
            .login()!
            .then((data: unknown) => {
              loginSubscriber.next(data);
              loginSubscriber.complete();
            })
            .catch((loginError: unknown) => {
              loginSubscriber.error(loginError);
            });
        }).pipe(
          mergeMap(() => {
            return next.handle(
              request.clone({
                setHeaders: { Authorization: `Bearer ${authenticationService.getAccessToken()}` },
              }),
            );
          }),
          catchError((_loginError: HttpErrorResponse) => {
            authenticationService.logout();
            startLoginWithoutAwaiting(oauthService);
            return throwError(response);
          }),
        );
      }),
      finalize(() => {
        this.isRefreshingToken = false;
      }),
    );
  }
}
